<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="emoji"
        image="🧘"
        :image-class="`transition-all duration-1000 ${isActive ? '-hue-rotate-160 saturate-40' : '-hue-rotate-160 saturate-20'}`"
        title="Anti-Stress"
        subtitle="Toolbox"
        back="/tools"
        disable-edit-mode="true"
      >
      </round-header>
      <div class="flex justify-center">
        <div class="w-full max-w-[1024px] p-4 px-8 md:px-20">
          <div v-if="!selectedDuration" class="space-y-4 text-center">
            <h2 class="font-fredoka font-outline-1 text-2xl text-white">
              How much time do you have?
            </h2>
            <div class="grid grid-cols-3 gap-4">
              <button
                v-for="i in [1, 3, 5]"
                :key="i"
                class="border-border-positive text-border-positive flex cursor-pointer flex-col items-center rounded-xl border-2 bg-[#ffffff33] p-4"
                @click="() => handleDurationSelect(i)"
              >
                <Timer class="h-6 w-6" />
                <span>{{ i }} Minute{{ i > 0 ? 's' : '' }}</span>
              </button>
            </div>
          </div>

          <div v-else-if="currentExercise" class="space-y-6">
            <div class="flex items-center gap-4">
              <component :is="currentExercise.icon" class="h-6 w-6 text-gray-600" />
              <div>
                <h3 class="font-fredoka font-outline-1 text-lg text-white">
                  {{ currentExercise.title }}
                </h3>
                <p class="font-outline-1 text-sm text-white">
                  {{ selectedDuration }} minute {{ currentExercise.type }} exercise
                </p>
                <div
                  v-if="currentExercise.audio"
                  class="text-md mt-1 inline-block rounded-xl bg-[#ffffff99] px-3 py-1 text-gray-500 opacity-80"
                >
                  <Headphones class="mr-1 mb-1 inline-block w-4" /> Audio included
                </div>
              </div>
            </div>

            <div class="rounded-xl bg-[#ffffff66] p-4 text-white">
              <h4 class="font-fredoka font-outline-1">Instructions</h4>
              <ol class="font-outline-1 list-decimal space-y-1 pl-4">
                <li v-for="(step, index) in currentExercise.instructions" :key="index">
                  {{ step }}
                </li>
              </ol>
              <p class="font-outline-1 mt-2 text-sm">
                <strong>Benefits:</strong> {{ currentExercise.benefits }}
              </p>
            </div>

            <div class="space-y-2 text-center">
              <div class="relative h-10 overflow-hidden rounded-xl bg-[#ffffff99]">
                <div
                  :style="{ width: progress + '%' }"
                  class="bg-bg-positive absolute top-0 left-0 h-10 w-full rounded-xl transition-all duration-1000 ease-linear"
                ></div>
                <div
                  class="font-fredoka font-outline-1 relative z-10 pt-1 text-3xl text-white transition-all duration-1000"
                  :class="exerciseStarted ? 'opacity-20' : ''"
                >
                  {{ formatTime(timeLeft) }}
                </div>
              </div>
              <div class="mt-4 flex justify-center gap-4">
                <button
                  v-if="!exerciseStarted"
                  class="bg-fill-positive hover:bg-bg-positive cursor-pointer rounded-xl px-4 py-2 text-white transition-all duration-200"
                  @click="startTimer"
                >
                  <PlayCircle class="mr-1 inline h-5 w-5" />
                  Start
                </button>
                <template v-else>
                  <button
                    class="bg-bg-positive hover:bg-fill-positive flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border text-white transition-all duration-200"
                    @click="pauseTimer"
                  >
                    <PauseCircle v-if="isActive" class="h-6 w-6" />
                    <PlayCircle v-else class="h-6 w-6" />
                  </button>
                  <button
                    class="bg-bg-positive hover:bg-fill-positive flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border text-white transition-all duration-200"
                    @click="resetExercise"
                  >
                    <RotateCcw class="h-5 w-5" />
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div v-if="selectedDuration && false" class="mt-6 flex justify-between">
            <button
              class="font-outline-1 text-sm text-white hover:underline"
              @click="
                () => {
                  selectedDuration = null;
                  isActive = false;
                }
              "
            >
              Back
            </button>
            <button
              v-if="selectedDuration"
              class="font-outline-1 rounded border border-white px-3 py-1 text-sm text-white"
              @click="getNewExercise"
            >
              Try different exercise
            </button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue';
import {
  PlayCircle,
  PauseCircle,
  RotateCcw,
  Timer,
  Brain,
  Wind,
  Activity,
  Headphones,
} from 'lucide-vue-next';
import { IonContent, IonPage, onIonViewWillLeave } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import * as Sentry from '@sentry/vue';

const selectedDuration = ref<number | null>(null);
const currentExercise = ref<Exercise | null>(null);
const timeLeft = ref<number>(0);
const isActive = ref<boolean>(false);
const progress = ref<number>(0);
const exerciseStarted = ref<boolean>(false);

let interval: any = null;
const totalTime = ref<number>(0);
const bell: HTMLAudioElement = new Audio('/sounds/bell.mp3');
bell.load(); // Preload to avoid delay

onUnmounted(() => {
  clearInterval(interval);
  bell.pause();
});

watch([isActive, timeLeft], () => {
  if (isActive.value && timeLeft.value > 0) {
    interval = setTimeout(() => {
      timeLeft.value--;
      progress.value = ((totalTime.value - timeLeft.value) / totalTime.value) * 100;
    }, 1000);
  } else if (isActive.value && timeLeft.value === 0) {
    bell.play();
    isActive.value = false;
  } else {
    clearInterval(interval);
  }
});

const keepScreenOn = async () => {
  try {
    await ScreenOrientation.addListener('screenOrientationChange', () => {});
    await ScreenOrientation.lock({ orientation: 'portrait' });
  } catch (error) {
    Sentry.captureException(error);
  }
};

const releaseScreenLock = async () => {
  try {
    await ScreenOrientation.removeAllListeners();
    await ScreenOrientation.unlock();
  } catch (error) {
    Sentry.captureException(error);
  }
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const handleDurationSelect = (duration: number) => {
  selectedDuration.value = duration;
  timeLeft.value = duration * 60;
  totalTime.value = duration * 60;
  progress.value = 0;
  exerciseStarted.value = false;
  currentExercise.value = getRandomExercise(duration);
};

const startTimer = async () => {
  if (bell) {
    bell.play().catch(() => {
      console.log('blocked!');
    });
    bell.pause();
    bell.currentTime = 0;
  }

  const exercise = currentExercise.value as Exercise;
  if (exercise.audio != null) {
    exercise.audio.play().catch(() => {
      console.log('blocked!');
    });
  }

  isActive.value = true;
  exerciseStarted.value = true;
  await keepScreenOn();
};

const pauseTimer = async () => {
  isActive.value = !isActive.value;
  const exercise = currentExercise.value as Exercise;
  if (exercise.audio != null) {
    if (isActive.value) {
      exercise.audio.play();
      await keepScreenOn();
    } else {
      exercise.audio.pause();
      await releaseScreenLock();
    }
  }
};

const resetExercise = async () => {
  isActive.value = false;
  timeLeft.value = selectedDuration.value! * 60;
  progress.value = 0;
  exerciseStarted.value = false;

  const exercise = currentExercise.value as Exercise;
  if (exercise?.audio != null) {
    exercise.audio.load();
    exercise.audio.pause();
  }

  selectedDuration.value = null;
  await releaseScreenLock();
};

const getNewExercise = () => {
  if (selectedDuration.value) {
    currentExercise.value = getRandomExercise(selectedDuration.value);
    resetExercise();
  }
};

const getRandomExercise = (duration: number) => {
  const exercises = {
    1: oneMinuteExercises,
    3: threeMinuteExercises,
    5: fiveMinuteExercises,
  };
  const list = exercises[duration as keyof typeof exercises];
  return list[Math.floor(Math.random() * list.length)];
};

interface Exercise {
  id: string;
  type: string;
  title: string;
  instructions: string[];
  benefits: string;
  icon: any;
  audio?: HTMLAudioElement;
}

const oneMinuteExercises: Exercise[] = [
  {
    id: '1m-med-1',
    type: 'meditation',
    title: 'One-Minute Mindfulness',
    instructions: [
      'Sit comfortably with your back straight',
      'Close your eyes and take a deep breath',
      'Focus only on your breathing for one minute',
      'When your mind wanders, gently bring it back to your breath',
      'Notice the sensation of air entering and leaving your nostrils',
    ],
    benefits: 'Quickly centers your attention and reduces mental chatter',
    icon: Brain,
    audio: new Audio('/sounds/retryli_1min_mindfulness.mp3'),
  },
  {
    id: '1m-phy-1',
    type: 'physical',
    title: 'Desk Stretches',
    instructions: [
      'Remain seated and extend your arms overhead',
      'Interlace your fingers and stretch upward',
      'Lean gently from side to side',
      'Roll your shoulders backward and forward',
      'Gently tilt your head from side to side',
    ],
    benefits: 'Releases tension in your upper body and improves circulation',
    icon: Activity,
  },
  {
    id: '1m-bre-1',
    type: 'breathing',
    title: '4-7-8 Breathing',
    instructions: [
      'Sit comfortably with your back straight',
      'Inhale quietly through your nose for 4 seconds',
      'Hold your breath for 7 seconds',
      'Exhale completely through your mouth for 8 seconds',
      'Repeat this cycle for the remaining time',
    ],
    benefits: 'Quickly reduces anxiety and helps regain focus',
    icon: Wind,
    audio: new Audio('/sounds/retryli_1min_breathing.mp3'),
  },
];

const threeMinuteExercises: Exercise[] = [
  {
    id: '3m-med-1',
    type: 'meditation',
    title: 'Body Scan',
    instructions: [
      'Sit or lie down comfortably',
      'Close your eyes and take a few deep breaths',
      'Starting from your toes, bring awareness to each part of your body',
      'Notice any sensations without trying to change them',
      'Gradually move up through your legs, torso, arms, and head',
      'If you notice tension anywhere, breathe into that area',
    ],
    benefits: 'Increases body awareness and releases physical tension',
    icon: Brain,
  },
  {
    id: '3m-phy-1',
    type: 'physical',
    title: 'Standing Energizer',
    instructions: [
      'Stand up and shake out your limbs for 20 seconds',
      'Do 10 gentle jumping jacks',
      'March in place with high knees for 30 seconds',
      'Roll your shoulders backward and forward 10 times',
      'Finish with 5 deep breaths with arms raising on inhale, lowering on exhale',
    ],
    benefits: 'Boosts energy, improves circulation, and releases tension',
    icon: Activity,
  },
  {
    id: '3m-bre-1',
    type: 'breathing',
    title: 'Box Breathing',
    instructions: [
      'Sit comfortably with your back straight',
      'Inhale through your nose for 4 seconds',
      'Hold your breath for 4 seconds',
      'Exhale through your mouth for 4 seconds',
      'Hold your breath for 4 seconds',
      'Repeat this square pattern for the remaining time',
    ],
    benefits: 'Calms the nervous system and improves concentration',
    icon: Wind,
  },
];

const fiveMinuteExercises: Exercise[] = [
  {
    id: '5m-med-1',
    type: 'meditation',
    title: 'Loving-Kindness Meditation',
    instructions: [
      'Sit comfortably with your eyes closed',
      'Take a few deep breaths to center yourself',
      "Think of yourself and repeat: 'May I be happy, may I be healthy, may I be safe'",
      'Think of someone you care about and repeat the same phrases for them',
      'Think of someone neutral in your life and repeat the phrases',
      'Finally, think of all beings and wish them happiness, health, and safety',
      'End by taking a few deep breaths',
    ],
    benefits: 'Cultivates compassion, reduces negative emotions, and promotes positive feelings',
    icon: Brain,
  },
  {
    id: '5m-phy-1',
    type: 'physical',
    title: 'Mini Yoga Flow',
    instructions: [
      'Stand tall with feet hip-width apart',
      'Raise arms overhead while inhaling (Mountain Pose)',
      'Fold forward at the hips while exhaling (Forward Fold)',
      'Step back into a plank position',
      'Lower to the floor and push up into Cobra pose',
      "Push back to Child's pose",
      'Return to standing and repeat the sequence',
      'Move at your own pace, coordinating movement with breath',
    ],
    benefits: 'Improves flexibility, releases tension, and centers the mind',
    icon: Activity,
  },
  {
    id: '5m-bre-1',
    type: 'breathing',
    title: 'Alternate Nostril Breathing',
    instructions: [
      'Sit comfortably with your back straight',
      'Rest your left hand on your left knee',
      'Place your right thumb against your right nostril',
      'Inhale deeply through your left nostril',
      'Close your left nostril with your ring finger',
      'Release your right nostril and exhale',
      'Inhale through your right nostril',
      'Close your right nostril and exhale through your left',
      'Continue alternating for the remaining time',
    ],
    benefits: 'Balances the nervous system, improves focus, and reduces stress',
    icon: Wind,
  },
];

onIonViewWillLeave(() => {
  resetExercise();
});
</script>

<style scoped>
/* Optional scoped styles if needed */
</style>
